"use client"

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { Package, ShoppingCart, BarChart2, HandCoins, Menu } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useStore } from '@/lib/store'
import { canOpen } from '@/lib/roles'
import { useFeature } from '@/hooks/use-feature'
import { useT, type Key } from '@/lib/i18n'
import { OrderBadge, useOrderAlertBadge } from '@/components/order-alerts'
import { SupportBadge, useSupportAlertBadge } from '@/components/support-alerts'

// Native-app-style bottom tab bar — phones only (md:hidden). The web/desktop
// layout is untouched: sidebar stays the desktop navigation.
//
// Since 2026-09-11 it is a FLOATING CAPSULE inset from the edges (the iOS 26 /
// Material 3 Expressive shape) with page content scrolling underneath it,
// instead of a strip welded to the bottom of the screen.
//
// Rules baked in:
//  • ≤5 items, icon + label (bottom-nav conventions; no icon-only mystery meat)
//  • NO backdrop-blur — this is always-visible chrome and blur overheated
//    weak shop GPUs twice (see PROJECT_OVERVIEW → Performance). `.tab-capsule`
//    is near-opaque; the depth comes from the shadow, not from glass.
//  • navigation fires 100+×/day → no entrance animations; only an instant
//    press-down response (Apple: respond on pointer-down) + a 150ms tint
//  • sits above the gesture bar (safe-area); `--tab-capsule-top` in
//    globals.css is where it ENDS, and everything that must clear it reads that
const TABS: readonly { href: string; icon: React.ElementType; label: Key }[] = [
  { href: '/warehouse',  icon: Package,      label: 'tab.warehouse' },
  { href: '/pos',        icon: ShoppingCart, label: 'tab.pos' },
  { href: '/accounting', icon: BarChart2,    label: 'tab.accounting' },
  { href: '/credit',     icon: HandCoins,    label: 'tab.credit' },
]

// Routes reachable only through the drawer — they light up the "მეტი" tab.
const MORE_ROUTES = ['/suppliers', '/staff', '/tools', '/guide', '/settings', '/platform', '/repairs', '/orders']

export function MobileTabBar() {
  const t = useT()
  const pathname = usePathname()
  const setMobileSidebarOpen = useStore((s) => s.setMobileSidebarOpen)
  const moreActive = MORE_ROUTES.some((r) => pathname.startsWith(r))
  // Only the tabs this role may open (the same `canOpen` as the sidebar and
  // the route guard) — a cashier used to see „საწყობი" and „ბუღალტ." and be
  // bounced; the accountant (094) has no till. Before the role is known, all
  // four, so an owner's bar does not jump on every start.
  const userRole = useStore((s) => s.userRole)
  // „მარტივი რეჟიმი": no ნისია tab in a shop that does not sell on credit.
  const creditOn = useFeature('credit')
  const tabs = (userRole ? TABS.filter((t) => canOpen(t.href, userRole)) : TABS)
    .filter((t) => t.href !== '/credit' || creditOn)
  // online orders live behind „მეტი": their unanswered count shows on it
  const orders = useOrderAlertBadge()
  // 1.45 — so does „პლატფორმა"'s waiting help requests (the platform admin only), on the other corner
  const support = useSupportAlertBadge()

  return (
    <nav
      aria-label={t('tab.nav')}
      className="md:hidden fixed inset-x-3 bottom-[calc(env(safe-area-inset-bottom)+0.5rem)] z-40 tab-capsule rounded-[26px]"
    >
      <div className="grid h-16 px-1" style={{ gridTemplateColumns: `repeat(${tabs.length + 1}, minmax(0, 1fr))` }}>
        {tabs.map(({ href, icon: Icon, label }) => {
          const active = pathname.startsWith(href)
          return (
            <Link
              key={href}
              href={href}
              aria-current={active ? 'page' : undefined}
              className="group flex flex-col items-center justify-center gap-1 select-none [-webkit-tap-highlight-color:transparent]"
            >
              <span className={cn(
                'flex items-center justify-center h-8 w-14 rounded-full transition-[background-color,color,scale] duration-150 group-active:scale-[0.92]',
                active ? 'bg-primary/12 text-primary dark:bg-primary/20' : 'text-muted-foreground'
              )}>
                <Icon className="size-5" strokeWidth={active ? 2.4 : 2} />
              </span>
              <span className={cn(
                'text-[12px] leading-none font-bold transition-colors duration-150',
                active ? 'text-primary' : 'text-muted-foreground'
              )}>
                {t(label)}
              </span>
            </Link>
          )
        })}

        <button
          onClick={() => setMobileSidebarOpen(true)}
          aria-label={t('tab.moreAria')}
          className="group flex flex-col items-center justify-center gap-1 select-none [-webkit-tap-highlight-color:transparent]"
        >
          <span className={cn(
            'relative flex items-center justify-center h-8 w-14 rounded-full transition-[background-color,color,scale] duration-150 group-active:scale-[0.92]',
            moreActive ? 'bg-primary/12 text-primary dark:bg-primary/20' : 'text-muted-foreground'
          )}>
            <Menu className="size-5" strokeWidth={moreActive ? 2.4 : 2} />
            <OrderBadge count={orders.count} pulse={orders.pulse} className="absolute -right-0.5 -top-1.5" />
            <SupportBadge count={support.count} pulse={support.pulse} className="absolute -left-0.5 -top-1.5" />
          </span>
          <span className={cn(
            'text-[12px] leading-none font-bold transition-colors duration-150',
            moreActive ? 'text-primary' : 'text-muted-foreground'
          )}>
            {t('tab.more')}
          </span>
        </button>
      </div>
    </nav>
  )
}
